import { createContext, useContext, useMemo } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { GET_BARRIERS, GET_PARKING_BY_ID } from "../api/graphql/GetParkingList";
import { mapParkingListResponse } from "../api/mapResponse";
import { useCookies } from "react-cookie";
import { useSearchParams } from "react-router-dom";
import { GET_ORDER_BY_ID } from "../api/graphql/Order";
import { MILLIS_IN_SECOND } from "util/consts/datetime";
import {
  find,
  propEq,
  safe,
  and,
  isObject,
  hasProps,
  Async,
  maybeToEither,
  getPath,
} from "crocks";
import { TOGGLE_GATE } from "api/graphql/OneTimeOrder";
import { useTranslation } from "react-i18next";

const GateContext = createContext();

const GateContextProvider = ({ children }) => {
  const { t } = useTranslation();
  const [cookies, setCookie] = useCookies(["user"]);
  let [searchParams, setSearchParams] = useSearchParams();
  const orderId = cookies.order || searchParams.get("order");
  const { phone, plate } = cookies;

  const { data: orderData, ...orderQuery } = useQuery(GET_ORDER_BY_ID, {
    variables: {
      id: orderId,
    },
    fetchPolicy: "network-only",
    pollInterval: 2 * MILLIS_IN_SECOND,
  });

  const parkingId = useMemo(
    () =>
      orderData?.order_by_pk?.parking?.id ||
      cookies.parking ||
      searchParams.get("id"),
    [orderData, cookies, searchParams],
  );

  const { data, ...dataQuery } = useQuery(GET_PARKING_BY_ID, {
    variables: {
      id: parkingId,
    },
    notifyOnNetworkStatusChange: true,
  });

  const { data: barriersData, ...barriersQuery } = useQuery(GET_BARRIERS, {
    variables: {
      id: parkingId,
    },
    notifyOnNetworkStatusChange: true,
  });

  const item = useMemo(
    () => mapParkingListResponse(data?.parking_by_pk || {}),
    [data?.parking_by_pk],
  );
  const gates = useMemo(
    () => data?.parking_by_pk?.gates || [],
    [data?.parking_by_pk?.gates],
  );
  const barrier = useMemo(
    () => barriersData?.gate?.[0] || null,
    [barriersData],
  );

  const [toggleGate] = useMutation(TOGGLE_GATE, {
    refetchQueries: ["GET_ACTIVE_ORDER", "CheckHasActiveOrder"],
  });

  return (
    <GateContext.Provider
      value={{
        loading: dataQuery.loading || barriersQuery.loading,
        parkingId,
        orderId,
        item,
        gates,
        exitGate: maybeToEither(
          t("could_not_find_exit_gate"),
          find(propEq("is_exit", true), gates).chain(
            safe(and(isObject, hasProps(["uid", "upass"]))),
          ),
        ),
        barriers: barriersData,
        firstBarrier: maybeToEither(
          t("could_not_find_first_barrier"),
          getPath(["gate", 0], barriersData).chain(
            safe(and(isObject, hasProps(["uid", "upass"]))),
          ),
        ),
        toggleGate: Async.fromPromise(toggleGate),
        order: orderData?.order_by_pk || {},
        orderQueryResult: orderQuery,
        phone,
        plate,
        /** @deprecated use firstBarrier instead */
        barrier,
      }}
    >
      {children}
    </GateContext.Provider>
  );
};

const useGates = () => useContext(GateContext);

export { GateContext, GateContextProvider, useGates };
